import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import AgoraRTC, { AgoraRTCProvider } from "agora-rtc-react";
import './index.css';
import App from './App';
import { AuthProvider } from './AuthContext';
import reportWebVitals from './reportWebVitals';
import { ErrorBoundary } from 'react-error-boundary'; // Add this import

// Initialize Agora client with enhanced settings
const agoraClient = AgoraRTC.createClient({ 
  mode: "live", 
  codec: "vp8",
  role: "host",
  optimization: {
    encoderConfig: {
      width: 640,
      height: 360,
      frameRate: 15,
      bitrateMin: 100,
      bitrateMax: 1000
    }
  }
});

// Error Fallback component
const ErrorFallback = ({ error, resetErrorBoundary }) => (
  <div className="flex items-center justify-center min-h-screen bg-gray-50">
    <div className="text-center">
      <h2 className="text-xl font-bold text-red-600 mb-4">Something went wrong</h2>
      <p className="text-gray-600 mb-4">{error.message}</p>
      <button
        onClick={resetErrorBoundary}
        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
      >
        Try again
      </button>
    </div>
  </div>
);

// Global error handlers with enhanced logging
const handleError = (error, errorInfo = null) => {
  console.error('Uncaught error:', {
    error,
    errorInfo,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    url: window.location.href
  });

  // Show error boundary
  const errorBoundary = document.getElementById('error-boundary');
  if (errorBoundary) {
    errorBoundary.classList.remove('hidden');
  }
};

// Global error listeners
window.onerror = (message, source, lineno, colno, error) => {
  handleError(error || message);
  return false;
};

window.onunhandledrejection = (event) => {
  handleError(event.reason);
  return false;
};

// Create root with error handling
const createRoot = () => {
  try {
    const container = document.getElementById('root');
    if (!container) {
      throw new Error('Root element not found');
    }
    return ReactDOM.createRoot(container);
  } catch (error) {
    handleError(error);
    return null;
  }
};

// Initialize application
const initializeApp = async () => {
  try {
    // Request permissions early
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ 
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          } 
        });

        // Keep reference for iOS
        window.initialAudioStream = stream;
        console.log('Audio permissions granted');
      } catch (error) {
        console.error('Initial microphone permission check failed:', error);
      }
    }

    // Audio context setup for iOS
    if (/iPhone|iPad|iPod/.test(navigator.platform)) {
      window.AudioContext = window.AudioContext || window.webkitAudioContext;
      window.audioContext = new AudioContext();
    }

     // Create and render root
     const root = createRoot();
     if (root) {
       root.render(
         <React.StrictMode>
           <ErrorBoundary FallbackComponent={ErrorFallback}>
             <BrowserRouter>
               <AuthProvider>
                 <AgoraRTCProvider client={agoraClient}>
                   <App />
                 </AgoraRTCProvider>
               </AuthProvider>
             </BrowserRouter>
           </ErrorBoundary>
         </React.StrictMode>
       );
     }
   } catch (error) {
     handleError(error);
   }
 };

// Initialize the app
initializeApp();

// Performance monitoring with enhanced error handling
const reportWebVitalsCallback = (metric) => {
  try {
    if (process.env.NODE_ENV === 'development') {
      console.log('Web Vitals:', metric);
    }
    // Send metrics to analytics service
    if (metric.name === 'FID' && metric.value > 100) {
      console.warn('Poor FID detected:', metric);
    }
  } catch (error) {
    console.error('Error reporting web vitals:', error);
  }
};

reportWebVitals(reportWebVitalsCallback);

// Browser navigation handling
window.onpopstate = function(event) {
  // Prevent going back after logout
  const token = localStorage.getItem('authToken');
  if (!token && window.location.pathname !== '/login') {
    window.history.pushState(null, '', '/login');
    window.location.reload();
  }
};

// Cleanup on page unload
window.onbeforeunload = function() {
  try {
    // Clean up all connections
    if (window.socket) {
      window.socket.disconnect();
    }

    // Clean up audio
    if (window.audioContext) {
      window.audioContext.close();
    }

    if (window.initialAudioStream) {
      window.initialAudioStream.getTracks().forEach(track => track.stop());
    }
  } catch (error) {
    console.error('Cleanup error:', error);
  }
};

// Add device detection
if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
  document.documentElement.dataset.theme = 'dark';
}

// Online/offline detection
window.addEventListener('online', () => {
  document.documentElement.dataset.online = 'true';
});

window.addEventListener('offline', () => {
  document.documentElement.dataset.online = 'false';
});

// Mobile viewport fix
const updateViewportHeight = () => {
  const vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
};

window.addEventListener('resize', updateViewportHeight);
window.addEventListener('orientationchange', updateViewportHeight);
updateViewportHeight();

// Export for testing
export { handleError, createRoot, initializeApp };